import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import {
  Building2,
  Eye,
  Lock,
  Unlock,
  Trash2,
  CheckCircle2,
  XCircle,
  Users,
  WifiOff,
  UserPlus,
  Gift,
  Check,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export const money = (n, currency) =>
  new Intl.NumberFormat(currency === "USD" ? "en-US" : "es-CR", { style: "currency", currency: currency === "USD" ? "USD" : "CRC", minimumFractionDigits: 2 }).format(n || 0);

export const crc = (n) => money(n, "CRC");

// Rueda una fecha mes a mes (conservando el día) hasta hoy o futuro: las fechas de cobro son recurrentes
export function rollForward(iso) {
  if (!iso) return "";
  let d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return "";
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  let guard = 0;
  while (d < hoy && guard < 240) {
    const y = d.getFullYear();
    const m = d.getMonth();
    const last = new Date(y, m + 2, 0).getDate();
    d = new Date(y, m + 1, Math.min(d.getDate(), last));
    guard++;
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export const fmtDate = (d) => {
  if (!d) return "—";
  const dt = String(d).includes("T") ? new Date(d) : new Date(d + "T00:00:00");
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("es-CR", { day: "numeric", month: "short", year: "numeric" });
};

const payStatus = {
  approved: { label: "Aprobado", cls: "bg-vteal-light/20 text-vteal-light border-vteal-light/30" },
  pending: { label: "Pendiente", cls: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  rejected: { label: "Rechazado", cls: "bg-red-500/20 text-red-300 border-red-500/30" },
};

function BillingEditor({ client, billing, onSave }) {
  const [form, setForm] = useState({ frequency: "mensual", amount: "", invoice: false, currency: "CRC", client_code: "", pay_date_1: "", pay_date_2: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      frequency: billing?.frequency || "mensual",
      amount: billing?.amount || "",
      invoice: !!billing?.invoice,
      currency: billing?.currency === "USD" ? "USD" : "CRC",
      client_code: billing?.client_code || "",
      pay_date_1: billing?.pay_date_1 || "",
      pay_date_2: billing?.pay_date_2 || "",
    });
  }, [billing]);

  const amount = Number(form.amount) || 0;
  const totalConIva = amount * 1.13;
  const porCobro = form.frequency === "quincenal" ? (form.invoice ? totalConIva / 2 : amount / 2) : (form.invoice ? totalConIva : amount);
  const dateCls = "w-full h-9 rounded-md border border-white/15 bg-vnavy px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-vteal-light/50 [color-scheme:dark]";

  const submit = async () => {
    if (!amount) { toast.error("Ingrese el monto de la mensualidad."); return; }
    if (!form.pay_date_1) { toast.error("Ingrese la fecha de pago."); return; }
    if (form.frequency === "quincenal" && !form.pay_date_2) { toast.error("Ingrese la segunda fecha de pago."); return; }
    setSaving(true);
    await onSave(client, { ...form, amount });
    setSaving(false);
  };

  return (
    <div className="mt-4 rounded-xl border border-vteal-light/20 bg-vteal/[0.04] p-4" data-testid={`billing-editor-${client.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <p className="text-[11px] uppercase tracking-[0.15em] text-vteal-light font-semibold">Plan de cobro · Empresa Fee</p>
        <p className="text-gray-600 text-[11px]">Se guarda y aplica a los próximos cobros · puede modificarlo cuando lo necesite</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1.5">Plan de cobro</p>
          <div className="flex rounded-lg overflow-hidden border border-white/10">
            {["mensual", "quincenal"].map((f) => (
              <button key={f} type="button" onClick={() => setForm({ ...form, frequency: f, ...(f === "mensual" ? { pay_date_2: "" } : {}) })}
                data-testid={`billing-freq-${f}-${client.id}`}
                className={`flex-1 text-xs py-2 font-semibold capitalize transition-colors ${form.frequency === f ? "bg-vteal-light text-vnavy" : "text-gray-400 hover:text-white"}`}>
                {f}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1.5">Moneda</p>
          <div className="flex rounded-lg overflow-hidden border border-white/10">
            {[{ v: "CRC", label: "₡ Colones" }, { v: "USD", label: "$ Dólares" }].map((m) => (
              <button key={m.v} type="button" onClick={() => setForm({ ...form, currency: m.v })}
                data-testid={`billing-currency-${m.v.toLowerCase()}-${client.id}`}
                className={`flex-1 text-xs py-2 font-semibold transition-colors ${form.currency === m.v ? "bg-vteal-light text-vnavy" : "text-gray-400 hover:text-white"}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1.5">Mensualidad ({form.currency === "USD" ? "$" : "₡"})</p>
          <input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
            data-testid={`billing-amount-${client.id}`} placeholder="Ej. 50000" className={dateCls} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1.5">Código de facturación</p>
          <input type="text" maxLength={12} value={form.client_code} onChange={(e) => setForm({ ...form, client_code: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "") })}
            data-testid={`billing-code-${client.id}`} placeholder="Ej. AA93" className={dateCls} />
          <p className="text-gray-600 text-[10px] mt-1">Se usa en el N° de recibo: VCR-fecha-código</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1.5">Fecha de pago{form.frequency === "quincenal" ? " 1" : ""}</p>
          <input type="date" value={form.pay_date_1} onChange={(e) => setForm({ ...form, pay_date_1: e.target.value })}
            data-testid={`billing-date1-${client.id}`} className={dateCls} />
        </div>
        {form.frequency === "quincenal" && (
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1.5">Fecha de pago 2</p>
            <input type="date" value={form.pay_date_2} onChange={(e) => setForm({ ...form, pay_date_2: e.target.value })}
              data-testid={`billing-date2-${client.id}`} className={dateCls} />
          </div>
        )}
        <div className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-0.5">Factura electrónica</p>
            <p className="text-gray-600 text-xs">Sumar 13% IVA al cobro</p>
          </div>
          <Switch checked={form.invoice} onCheckedChange={(v) => setForm({ ...form, invoice: v })}
            data-testid={`billing-invoice-${client.id}`} className="data-[state=checked]:bg-vteal-light" />
        </div>
      </div>
      {amount > 0 && (
        <p className="text-gray-400 text-xs mt-3" data-testid={`billing-summary-${client.id}`}>
          {form.frequency === "quincenal" ? (
            <>Mensualidad {money(amount, form.currency)} → <span className="text-white font-semibold">2 pagos quincenales de {money(porCobro, form.currency)}</span>{form.invoice ? " (IVA 13% incluido en cada pago)" : ""}</>
          ) : (
            <>1 pago mensual de <span className="text-white font-semibold">{money(porCobro, form.currency)}</span>{form.invoice ? " (IVA 13% incluido)" : ""}</>
          )}
        </p>
      )}
      <div className="flex justify-end mt-3">
        <button onClick={submit} disabled={saving} data-testid={`billing-save-${client.id}`}
          className="px-5 py-2 rounded-full bg-vteal-light text-vnavy text-xs font-semibold hover:bg-white transition-colors disabled:opacity-60">
          {saving ? "Guardando..." : "Guardar cobro"}
        </button>
      </div>
    </div>
  );
}

function ClientCard({ client, onRefresh, onViewPayments, onToggleFree, onBlock, onUnblock, onDelete, billing, onSaveBilling }) {
  const blocked = client.status === "blocked";
  const free = client.is_free;
  return (
    <div className="rounded-2xl bg-vnavy-800 border border-white/10 p-6" data-testid={`client-card-${client.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-vteal/15 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-vteal-light" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-white font-semibold text-lg">{client.company_name}</h3>
              {blocked ? (
                <Badge className="border bg-red-500/20 text-red-300 border-red-500/30">Bloqueado</Badge>
              ) : (
                <Badge className="border bg-vteal-light/20 text-vteal-light border-vteal-light/30">Activa</Badge>
              )}
            </div>
            <p className="text-gray-500 text-xs mt-1">Registrado: {new Date(client.created_at).toLocaleDateString("es-CR")}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => onViewPayments(client)} data-testid={`view-payments-${client.id}`} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 text-gray-300 hover:border-vteal-light/40 transition-colors">
            <Eye className="h-3.5 w-3.5" /> Ver pagos
          </button>
          {blocked ? (
            <button onClick={() => onUnblock(client)} data-testid={`unblock-${client.id}`} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-vteal-light/30 text-vteal-light hover:bg-vteal-light/10 transition-colors">
              <Unlock className="h-3.5 w-3.5" /> Desbloquear
            </button>
          ) : (
            <button onClick={() => onBlock(client)} data-testid={`block-${client.id}`} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 text-amber-300 hover:border-amber-400/40 transition-colors">
              <Lock className="h-3.5 w-3.5" /> Bloquear
            </button>
          )}
          <button onClick={() => onDelete(client)} data-testid={`delete-client-${client.id}`} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 text-red-400 hover:border-red-500/40 transition-colors">
            <Trash2 className="h-3.5 w-3.5" /> Eliminar
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-xl bg-vnavy border border-white/5 px-4 py-3 mb-4">
        <div className="flex items-center gap-2">
          <Gift className="h-4 w-4 text-vteal-light" />
          <span className="text-sm text-gray-300">Empresa Fee <span className="text-gray-500">· Sin cobro por el sistema R.H</span></span>
        </div>
        <Switch
          checked={free}
          onCheckedChange={(v) => onToggleFree(client, v)}
          data-testid={`free-toggle-${client.id}`}
          className="data-[state=checked]:bg-vteal-light"
        />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
        <Field label="Céd. jurídica" value={client.ced_juridica || "—"} />
        <Field label="Correo" value={client.owner_email || "—"} />
        <Field label="Teléfono" value={client.phone || "—"} />
        <Field label="Último pago" value={client.last_payment_date ? `${fmtDate(client.last_payment_date)} · ${crc(client.last_payment_amount)}` : "Sin pagos"} />
        <Field label="Total pagado" value={crc(client.total_paid)} />
        {!free && <Field label="Plan" value={`${client.plan} · ${client.frequency}`} />}
      </div>

      <div className="grid grid-cols-3 gap-3 rounded-xl bg-vnavy border border-white/5 p-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1 flex items-center gap-1"><Users className="h-3 w-3" /> Colaboradores</p>
          <p className="text-white font-semibold">{client.collaborators}</p>
        </div>
        {free ? (
          <>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1">Mensualidad</p>
              <p className="text-white font-semibold" data-testid={`mensualidad-${client.id}`}>{billing?.amount ? money(billing.amount, billing?.currency) : "—"}</p>
              {billing?.amount > 0 && (
                <p className="text-gray-600 text-[10px] mt-0.5" data-testid={`mensualidad-detalle-${client.id}`}>
                  {billing.frequency === "quincenal"
                    ? `2 pagos de ${money(billing.invoice ? (billing.amount * 1.13) / 2 : billing.amount / 2, billing?.currency)}${billing.invoice ? " c/IVA" : ""}`
                    : billing.invoice
                    ? `${money(billing.amount * 1.13, billing?.currency)} c/IVA`
                    : "Pago mensual"}
                </p>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1">Próximo pago</p>
              <p className="text-amber-300 font-semibold" data-testid={`proximo-pago-${client.id}`}>
                {billing?.pay_date_1 ? `${fmtDate(rollForward(billing.pay_date_1))}${billing.pay_date_2 ? ` · ${fmtDate(rollForward(billing.pay_date_2))}` : ""}` : "—"}
              </p>
            </div>
          </>
        ) : (
          <>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1">Total del ciclo</p>
              <p className="text-white font-semibold">{crc(client.cycle_total)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1">Próximo pago</p>
              <p className="text-amber-300 font-semibold">{fmtDate(client.next_payment_date)}</p>
            </div>
          </>
        )}
      </div>

      {free && <BillingEditor client={client} billing={billing} onSave={onSaveBilling} />}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="rounded-lg bg-vnavy border border-white/5 px-4 py-2.5">
      <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-0.5">{label}</p>
      <p className="text-gray-200 text-sm truncate" title={value}>{value}</p>
    </div>
  );
}

function ClientSimpleRow({ client, onToggleFree }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl bg-vnavy-800 border border-white/10 px-5 py-4" data-testid={`client-row-${client.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-9 w-9 rounded-lg bg-vteal/15 flex items-center justify-center shrink-0">
          <Building2 className="h-4 w-4 text-vteal-light" />
        </div>
        <div className="min-w-0">
          <p className="text-white font-semibold truncate">{client.company_name}</p>
          <p className="text-gray-500 text-xs">Registrado: {new Date(client.created_at).toLocaleDateString("es-CR")}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs text-gray-500">Empresa Fee</span>
        <Switch checked={client.is_free} onCheckedChange={(v) => onToggleFree(client, v)} data-testid={`free-toggle-${client.id}`} className="data-[state=checked]:bg-vteal-light" />
      </div>
    </div>
  );
}

function RegistrationRequests({ items, onApprove, onReject }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-8 rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-6" data-testid="registration-requests">
      <div className="flex items-center gap-2 mb-5">
        <UserPlus className="h-5 w-5 text-amber-300" />
        <h3 className="text-white font-semibold text-lg">Solicitudes de Registro</h3>
        <Badge className="border bg-amber-500/20 text-amber-300 border-amber-500/30">{items.length} pendiente{items.length !== 1 ? "s" : ""}</Badge>
      </div>
      <div className="space-y-4">
        {items.map((c) => (
          <div key={c.id} className="rounded-xl bg-vnavy-800 border border-white/10 p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`registration-${c.id}`}>
            <div className="min-w-0">
              <p className="text-white font-semibold">{c.company_name}</p>
              <p className="text-gray-500 text-xs mt-1">
                {c.owner_email || "sin correo"}
                {c.ced_juridica ? ` · Céd. ${c.ced_juridica}` : ""}
                {c.plan && c.plan !== "—" ? ` · ${c.plan}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => onApprove(c)} data-testid={`approve-reg-${c.id}`} className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg bg-vteal-light text-vnavy font-semibold hover:bg-white transition-colors">
                <Check className="h-3.5 w-3.5" /> Aprobar registro
              </button>
              <button onClick={() => onReject(c)} data-testid={`reject-reg-${c.id}`} className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-white/10 text-red-400 hover:border-red-500/40 transition-colors">
                <XCircle className="h-3.5 w-3.5" /> Rechazar
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SystemPanel({ system, view }) {
  const [clients, setClients] = useState([]);
  const [billingMap, setBillingMap] = useState({});
  const [connected, setConnected] = useState(true);
  const [connError, setConnError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payDialog, setPayDialog] = useState(null); // client
  const [payments, setPayments] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, bill] = await Promise.all([
        api.get(`/admin/clients?system=${system}`),
        api.get(`/admin/billing?system=${system}`),
      ]);
      setConnected(data.connected);
      setConnError(data.error);
      setClients(data.clients || []);
      const map = {};
      (bill.data || []).forEach((b) => { map[b.client_id] = b; });
      setBillingMap(map);
    } catch (err) {
      setConnected(false);
      setConnError(formatApiErrorDetail(err.response?.data?.detail) || "Error de conexión.");
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [system]);

  useEffect(() => {
    load();
  }, [load]);

  const openPayments = (client) => {
    setPayDialog(client);
    setPayments(client.payments || []);
  };

  const decide = async (paymentId, action) => {
    try {
      await api.post(`/admin/payments/${paymentId}/${action}?system=${system}`);
      toast.success(action === "approve" ? "Pago aprobado." : "Pago rechazado.");
      setPayDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo actualizar el pago.");
    }
  };

  const block = async (client) => {
    try {
      await api.post(`/admin/clients/${client.id}/block?system=${system}`);
      toast.success(`${client.company_name} bloqueado.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo bloquear.");
    }
  };
  const unblock = async (client) => {
    try {
      await api.post(`/admin/clients/${client.id}/unblock?system=${system}`);
      toast.success(`${client.company_name} desbloqueado.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo desbloquear.");
    }
  };
  const del = async (client) => {
    if (!window.confirm(`¿Eliminar a ${client.company_name}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/clients/${client.id}?system=${system}`);
      toast.success("Cliente eliminado.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar.");
    }
  };

  const toggleFree = async (client, value) => {
    try {
      await api.post(`/admin/clients/${client.id}/set-free?system=${system}`, { is_free: value });
      toast.success(value ? `${client.company_name} marcada como gratuita.` : `${client.company_name} ya no es gratuita.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo actualizar el estado gratuito.");
    }
  };
  const saveBilling = async (client, form) => {
    try {
      const { data } = await api.put(`/admin/billing/${system}/${client.id}`, form);
      setBillingMap((m) => ({ ...m, [client.id]: data }));
      toast.success(`Cobro de ${client.company_name} guardado.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar el cobro.");
    }
  };

  const approveReg = async (client) => {
    try {
      await api.post(`/admin/clients/${client.id}/approve-registration?system=${system}`);
      toast.success(`Registro de ${client.company_name} aprobado.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo aprobar el registro.");
    }
  };
  const rejectReg = async (client) => {
    if (!window.confirm(`¿Rechazar y eliminar el registro de ${client.company_name}?`)) return;
    try {
      await api.post(`/admin/clients/${client.id}/reject-registration?system=${system}`);
      toast.success(`Registro de ${client.company_name} rechazado.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo rechazar el registro.");
    }
  };

  const pendientes = useMemo(() => clients.filter((c) => c.registration_status === "pending"), [clients]);
  const activos = useMemo(() => clients.filter((c) => c.registration_status !== "pending"), [clients]);
  const visibles = useMemo(() => {
    if (view === "fee") return activos.filter((c) => c.is_free);
    if (view === "sistema") return activos.filter((c) => !c.is_free);
    return activos;
  }, [activos, view]);

  return (
    <div>
      {!loading && !connected && (
        <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-5 flex items-start gap-3" data-testid={`conn-error-${system}`}>
          <WifiOff className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-200 font-semibold text-sm">No se pudo conectar con el sistema {system === "rrhh" ? "de Recursos Humanos" : "de Despacho Legal"}.</p>
            <p className="text-amber-200/70 text-xs mt-1">{connError}</p>
          </div>
        </div>
      )}

      <RegistrationRequests items={pendientes} onApprove={approveReg} onReject={rejectReg} />

      {loading ? (
        <p className="text-gray-500 py-12 text-center">Cargando clientes...</p>
      ) : visibles.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          <Building2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
          {pendientes.length > 0
            ? "No hay empresas aprobadas todavía."
            : !connected
            ? "Sin datos disponibles."
            : view === "fee"
            ? "No hay empresas Fee. Active el check 'Empresa Fee' en la sección Clientes."
            : view === "sistema"
            ? "No hay clientes del sistema."
            : "No hay clientes registrados en este sistema."}
        </div>
      ) : view === "todos" ? (
        <div className="space-y-3" data-testid={`clients-simple-list-${system}`}>
          {visibles.map((c) => (
            <ClientSimpleRow key={c.id} client={c} onToggleFree={toggleFree} />
          ))}
        </div>
      ) : (
        <div className="space-y-5" data-testid={`clients-list-${system}`}>
          {visibles.map((c) => (
            <ClientCard key={c.id} client={c} onRefresh={load} onViewPayments={openPayments} onToggleFree={toggleFree} onBlock={block} onUnblock={unblock} onDelete={del} billing={billingMap[c.id]} onSaveBilling={saveBilling} />
          ))}
        </div>
      )}

      <Dialog open={!!payDialog} onOpenChange={(o) => !o && setPayDialog(null)}>
        <DialogContent className="bg-vnavy-800 border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Histórico de Pagos {payDialog ? `· ${payDialog.company_name}` : ""}
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Revise los pagos del cliente y apruebe o rechace los que estén pendientes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
            {payments.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Este cliente no tiene pagos registrados.</p>
            ) : (
              payments.map((p) => {
                const st = payStatus[p.status] || payStatus.pending;
                return (
                  <div key={p.id} className="rounded-xl bg-vnavy border border-white/10 p-4" data-testid={`payment-${p.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-semibold">{crc(p.amount)}</span>
                          <Badge className={`border ${st.cls}`}>{st.label}</Badge>
                        </div>
                        <p className="text-gray-500 text-xs mt-1">
                          {fmtDate(p.date)} · {p.method} · {p.period}
                          {p.notes ? ` · ${p.notes}` : ""}
                        </p>
                      </div>
                      {p.status === "pending" && (
                        <div className="flex items-center gap-2">
                          <button onClick={() => decide(p.id, "approve")} data-testid={`approve-${p.id}`} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-vteal-light/15 text-vteal-light hover:bg-vteal-light/25 transition-colors">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Aprobar
                          </button>
                          <button onClick={() => decide(p.id, "reject")} data-testid={`reject-${p.id}`} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-red-500/15 text-red-300 hover:bg-red-500/25 transition-colors">
                            <XCircle className="h-3.5 w-3.5" /> Rechazar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ClientesModule() {
  return (
    <div>
      <Tabs defaultValue="todos">
        <TabsList className="bg-vnavy border border-white/10 mb-6">
          <TabsTrigger value="todos" data-testid="clients-view-todos" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
            Clientes
          </TabsTrigger>
          <TabsTrigger value="fee" data-testid="clients-view-fee" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
            Clientes Fee
          </TabsTrigger>
          <TabsTrigger value="sistema" data-testid="clients-view-sistema" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
            Clientes del Sistema
          </TabsTrigger>
        </TabsList>
        {["todos", "fee", "sistema"].map((v) => (
          <TabsContent key={v} value={v}>
            <Tabs defaultValue="rrhh">
              <TabsList className="bg-vnavy border border-white/10 mb-6">
                <TabsTrigger value="rrhh" data-testid="clients-tab-rrhh" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
                  Recursos Humanos
                </TabsTrigger>
                <TabsTrigger value="legal" data-testid="clients-tab-legal" className="data-[state=active]:bg-vteal-light data-[state=active]:text-vnavy">
                  Despacho Legal
                </TabsTrigger>
              </TabsList>
              <TabsContent value="rrhh">
                <SystemPanel system="rrhh" view={v} />
              </TabsContent>
              <TabsContent value="legal">
                <SystemPanel system="legal" view={v} />
              </TabsContent>
            </Tabs>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
